const React = require("react");
const h = React.createElement;

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const stripHtml = (html) => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

// Reconstruit categoriesID (voir buildCategoriesIDPayload de CJSyncModal)
// juste pour l'affichage -- dédupliqué par catID, noms joints " › ".
const summarizeCategories = (groups) => {
  const byId = new Map();
  for (const group of groups || []) {
    for (const entry of group.elakayCategories || []) {
      if (!entry?.catID || byId.has(String(entry.catID))) continue;
      byId.set(String(entry.catID), (entry.name || []).join(" ›") || null);
    }
  }
  return [...byId.values()].filter(Boolean);
};

// Étape 3 du CJSyncModal. Ne persiste jamais rien -- c'est une "simulation"
// en lecture seule du produit Elakay tel qu'il sera créé/mis à jour au clic
// sur Finalize Synchronization, construite localement à partir des mêmes
// clés que elakay_models/product.js (name/description/price/salePrice/
// shipping/categoriesID/dropshippingData) en combinant cjProduct (données CJ
// brutes) + categoriesValue (étape 1) + pricingResult (étape 2). Le seul état
// réellement appliqué à Finalize depuis cette étape est `autoSync` (bascule
// ci-dessous, cf. api.updateAutoSync dans CJSyncModal) -- tout le reste
// (comparaison de champs) est purement informatif : Finalize applique de
// toute façon toutes les valeurs CJ courantes des champs non verrouillés
// (sync complet), donc pas de sélection de champs ici pour ne pas dupliquer
// ce comportement avec un contrôle qui ne ferait rien de plus.
// Même résolution que resolveDefaultAddressShop côté serveur
// (cjProductSyncService.js) : l'adresse marquée par défaut, sinon la
// première -- affichée ici en lecture seule à partir de infoSource.address
// (déjà chargé côté page hôte, aucun appel réseau supplémentaire), pour que
// le seller voie quelle adresse deviendra product.addressShop au Finalize.
const resolveDefaultAddress = (addresses) => {
  const list = Array.isArray(addresses) ? addresses : [];
  return list.find((entry) => entry?.addressDefault) || list[0] || null;
};

const formatAddressLabel = (address) => {
  if (!address) return null;
  const shopName = Array.isArray(address.shopName) ? address.shopName[0] : address.shopName;
  if (shopName) return shopName;
  const parts = [address.Address1, address.city, address.State].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

const CJSynchronizationStep = ({ mode, cjProduct, compareData, categoriesValue, pricingResult, ownerId, sellerAddress, onValidChange, onSyncResult, t }) => {
  const [autoSync, setAutoSync] = React.useState(true);
  const autoSyncSeededRef = React.useRef(false);
  // Override par produit : paiement CJ automatique même si le réglage global
  // du vendeur (réglages CJ Dropshipping) reste sur manuel. Voir
  // Product.dropshippingData.autoPayOverride / applyPaymentModeGate(forceMode).
  const [autoPayOverride, setAutoPayOverride] = React.useState(false);
  const autoPayOverrideSeededRef = React.useRef(false);

  React.useEffect(() => {
    if (autoSyncSeededRef.current) return;
    if (typeof compareData?.autoSync !== "boolean") return;
    autoSyncSeededRef.current = true;
    setAutoSync(compareData.autoSync);
  }, [compareData]);

  React.useEffect(() => {
    if (autoPayOverrideSeededRef.current) return;
    if (typeof compareData?.dropshippingData?.autoPayOverride !== "boolean") return;
    autoPayOverrideSeededRef.current = true;
    setAutoPayOverride(compareData.dropshippingData.autoPayOverride);
  }, [compareData]);

  React.useEffect(() => {
    onValidChange && onValidChange(Boolean(cjProduct));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cjProduct]);

  React.useEffect(() => {
    onSyncResult && onSyncResult({ autoSync, autoPayOverride });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSync, autoPayOverride]);

  const categoryNames = summarizeCategories(categoriesValue);
  const dropshippingData = pricingResult?.dropshippingData || null;
  const imagesCount = (cjProduct?.images || []).length;
  const variantsCount = (cjProduct?.variants || compareData?.dropshippingData?.variants || []).length;
  const tags = compareData?.keysProduct || [];
  const shippingLabel = (value) => value
    ? t("h_cjwiz_sim_shipping_included", "Included in price")
    : t("h_cjwiz_sim_shipping_separate", "Charged separately");

  // Champs pilotés par les étapes 1 (Categories) et 2 (Shipping & Pricing) --
  // AUCUN d'eux ne fait partie de SYNCABLE_FIELDS (voir cjProductSyncService.js,
  // qui ne compare que name/description/price/images/category/tags/status/
  // variants), donc compareData.comparison ne les contient jamais. Reconstruits
  // ici localement à partir de categoriesValue/pricingResult -- c'est la valeur
  // qui sera réellement écrite au clic sur Finalize, pas la valeur déjà en base
  // (utile en mode="sync" où le produit existant a encore son ancien prix/ses
  // anciennes catégories tant que Finalize n'a pas tourné).
  const cjCategoryLeaf = Array.isArray(cjProduct?.category?.path) && cjProduct.category.path.length
    ? cjProduct.category.path[cjProduct.category.path.length - 1]
    : cjProduct?.category;
  const resolvedAddress = resolveDefaultAddress(sellerAddress);
  const addressLabel = formatAddressLabel(resolvedAddress);
  const pendingFieldRows = [
    {
      field: "owner",
      elakayValue: ownerId || t("h_cjwiz_sim_none", "None selected"),
      cjValue: "-",
    },
    {
      // addressShop (Product.js) -- adresse par défaut du seller, sinon la
      // première (voir resolveDefaultAddressShop côté serveur, appliqué au
      // même titre au Finalize/import/sync). Purement informatif ici.
      field: "addressShop",
      elakayValue: addressLabel || t("h_cjwiz_sim_none", "None selected"),
      cjValue: "-",
    },
    {
      field: "categoriesID",
      elakayValue: categoryNames.length ? categoryNames.join(", ") : t("h_cjwiz_sim_none", "None selected"),
      cjValue: cjCategoryLeaf?.name || "-",
    },
    {
      field: "price",
      elakayValue: pricingResult ? money(pricingResult.price) : "-",
      cjValue: cjProduct?.suggestedSalePrice != null ? money(cjProduct.suggestedSalePrice) : "-",
    },
    {
      field: "salePrice",
      elakayValue: pricingResult ? `${Number(pricingResult.salePrice) || 0}%` : "-",
      cjValue: "-",
    },
    {
      // dealTodays (Product.js) -- dérivé automatiquement de la remise
      // (salePrice > 0), pas un champ saisi séparément : voir updatePricing
      // (cjProducts.controller.js), seul appliqué au clic Finalize, comme
      // le reste de ce tableau.
      field: "dealTodays",
      elakayValue: pricingResult
        ? ((Number(pricingResult.salePrice) || 0) > 0
            ? t("h_cjwiz_dealtodays_yes", "Yes (discount applied)")
            : t("h_cjwiz_dealtodays_no", "No"))
        : "-",
      cjValue: "-",
    },
    {
      field: "costPrice",
      elakayValue: pricingResult ? money(pricingResult.costPrice) : "-",
      cjValue: cjProduct?.costPrice != null ? money(cjProduct.costPrice) : "-",
    },
    {
      field: "shipping",
      elakayValue: pricingResult ? shippingLabel(pricingResult.shipping) : "-",
      cjValue: "-",
    },
    {
      field: "shippingPrice",
      elakayValue: pricingResult ? money(pricingResult.shippingPrice) : "-",
      cjValue: "-",
    },
    {
      // ONE global variant selection, identical for every destination.
      field: "variantSelection",
      elakayValue: pricingResult?.variantSelection
        ? (pricingResult.variantSelection.mode === "all"
          ? t("h_cjms_sim_all_variants", "All variants ({count})", { count: variantsCount })
          : t("h_cjms_sim_some_variants", "{count} selected variants", { count: (pricingResult.variantSelection.selectedExternalVariantIds || []).length }))
        : "-",
      cjValue: String(variantsCount || "-"),
    },
    {
      field: "shippingDestinations",
      elakayValue: (dropshippingData?.shippingDestinations || []).length
        ? dropshippingData.shippingDestinations
          .map((d) => `${d.countryCode} ${money(d.shippingCost)} (${d.carrier})${d.sellActive === false ? ` - ${t("h_cjms_off", "off")}` : ""}`)
          .join(", ")
        : "-",
      cjValue: "-",
    },
  ];

  const checklist = [
    mode === "import"
      ? t("h_cjwiz_checklist_import", "Product imported from CJ Dropshipping")
      : t("h_cjwiz_checklist_sync", "Product synced with CJ's latest data"),
    t("h_cjwiz_checklist_categories", "{count} Elakay categor{plural} linked", {
      count: categoryNames.length, plural: categoryNames.length === 1 ? "y" : "ies",
    }),
    t("h_cjwiz_checklist_pricing", "Price & shipping applied as configured in Shipping & Pricing"),
    autoSync
      ? t("h_cjwiz_checklist_autosync_on", "Automatic synchronization turned ON")
      : t("h_cjwiz_checklist_autosync_off", "Automatic synchronization turned OFF"),
    autoPayOverride
      ? t("h_cjwiz_checklist_autopay_on", "Paiement CJ automatique forcé pour ce produit")
      : t("h_cjwiz_checklist_autopay_off", "Paiement CJ : suit le réglage global du vendeur"),
  ];

  return h("div", { className: "cjwiz-step" },
    h("div", { className: "cjwiz-step__intro" },
      h("h3", null, t("h_cjwiz_step3_title", "Synchronization")),
      h("p", { className: "cjds-muted" }, t(
        "h_cjwiz_step3_desc",
        "Review the product exactly as it will be created/updated. Nothing is applied yet -- click \"Finalize Synchronization\" to save everything at once."
      ))
    ),

    h("div", { className: "cjwiz-summary-card" },
      h("div", { className: "cjwiz-summary-card__header" },
        h("span", { className: "cjwiz-summary-card__status" }, t("h_cjwiz_sim_title", "Model Simulation"))
      ),
      h("div", { className: "cjwiz-summary-card__grid" },
        h("div", null, h("span", null, t("h_cjwiz_sim_name", "Name")), h("strong", null, cjProduct?.title || "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_category", "Categories")), h("strong", null, categoryNames.length ? categoryNames.join(", ") : t("h_cjwiz_sim_none", "None selected"))),
        h("div", null, h("span", null, t("h_cjwiz_sim_price", "Elakay price")), h("strong", null, pricingResult ? money(pricingResult.price) : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_discount", "Discount")), h("strong", null, pricingResult ? `${Number(pricingResult.salePrice) || 0}%` : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_shipping", "Shipping")), h("strong", null, pricingResult ? shippingLabel(pricingResult.shipping) : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_supplier_cost", "Total supplier cost")), h("strong", null, dropshippingData ? money(dropshippingData.totalSupplierCost) : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_profit", "Estimated profit")), h("strong", null, dropshippingData ? money(dropshippingData.calculatedProfit) : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_profit_margin", "Profit margin")), h("strong", null, dropshippingData ? `${Number(dropshippingData.profitMargin) || 0}%` : "-")),
        h("div", null, h("span", null, t("h_cjwiz_sim_images", "Images")), h("strong", null, `${imagesCount}`)),
        h("div", null, h("span", null, t("h_cjwiz_sim_variants", "Variants")), h("strong", null, `${variantsCount}`)),
        h("div", null, h("span", null, t("h_cjwiz_sim_tags", "Tags")), h("strong", null, tags.length ? tags.join(", ") : t("h_cjwiz_sim_none", "None selected")))
      ),
      cjProduct?.description ? h("p", { className: "cjds-muted", style: { marginTop: 14 } },
        stripHtml(cjProduct.description).slice(0, 220), stripHtml(cjProduct.description).length > 220 ? "..." : ""
      ) : null
    ),

    h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_checklist_title", "What happens when you click Finalize")),
      h("ul", { className: "cjds-sim-checklist" },
        ...checklist.map((entry, index) => h("li", { key: index }, entry))
      )
    ),

    h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_autosync_title", "Automatic synchronization")),
      h("div", { className: "cjwiz-mode-cards" },
        h("label", { className: `cjwiz-mode-card${autoSync ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-autosync", checked: autoSync, onChange: () => setAutoSync(true) }),
          h("div", null,
            h("strong", null, t("h_cjwiz_autosync_on_title", "Keep in sync automatically")),
            h("span", { className: "cjwiz-badge cjwiz-badge--muted" }, t("h_cjwiz_shipmode_default", "Default")),
            h("p", null, t("h_cjwiz_autosync_on_desc", "Elakay will periodically pull the latest stock/price/details from CJ Dropshipping for this product."))
          )
        ),
        h("label", { className: `cjwiz-mode-card${!autoSync ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-autosync", checked: !autoSync, onChange: () => setAutoSync(false) }),
          h("div", null,
            h("strong", null, t("h_cjwiz_autosync_off_title", "Manual synchronization only")),
            h("p", null, t("h_cjwiz_autosync_off_desc", "Nothing changes automatically -- you'll need to sync this product yourself from its Synchronization tab."))
          )
        )
      )
    ),

    h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_autopay_title", "Paiement CJ automatique pour ce produit")),
      h("div", { className: "cjwiz-mode-cards" },
        h("label", { className: `cjwiz-mode-card${!autoPayOverride ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-autopay", checked: !autoPayOverride, onChange: () => setAutoPayOverride(false) }),
          h("div", null,
            h("strong", null, t("h_cjwiz_autopay_off_title", "Respecter mon réglage global")),
            h("span", { className: "cjwiz-badge cjwiz-badge--muted" }, t("h_cjwiz_shipmode_default", "Default")),
            h("p", null, t("h_cjwiz_autopay_off_desc", "Les commandes contenant ce produit suivent le mode de paiement CJ configuré dans vos réglages (manuel ou automatique)."))
          )
        ),
        h("label", { className: `cjwiz-mode-card${autoPayOverride ? " cjwiz-mode-card--active" : ""}` },
          h("input", { type: "radio", name: "cjwiz-autopay", checked: autoPayOverride, onChange: () => setAutoPayOverride(true) }),
          h("div", null,
            h("strong", null, t("h_cjwiz_autopay_on_title", "Toujours tenter le paiement automatique")),
            h("p", null, t(
              "h_cjwiz_autopay_on_desc",
              "Même si votre réglage global est en mode manuel, Elakay tentera un paiement automatique pour les commandes contenant CE produit. Les autres produits de la même commande, non cochés, restent en mode manuel."
            )),
            h("p", { className: "cjds-muted" }, t(
              "h_cjwiz_autopay_on_limitation",
              "Rappel : l'API CJ Dropshipping ne propose aujourd'hui aucun paiement réel automatisé -- la tentative échouera et la commande passera en paiement manuel requis, comme pour le mode automatique global. Voir Réglages CJ Dropshipping."
            ))
          )
        )
      )
    ),

    h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_pending_fields_title", "Values to be saved (from Categories & Shipping/Pricing)")),
      h("p", { className: "cjds-muted" }, t(
        "h_cjwiz_pending_fields_note",
        "This is what will be written to the product when you click \"Finalize Synchronization\" -- not what's currently saved."
      )),
      h("div", { className: "cjds-table-wrap" },
        h("table", { className: "cjsync-table" },
          h("thead", null, h("tr", null,
            h("th", null, t("h_cjsync_col_field", "Field")),
            h("th", null, t("h_cjsync_col_elakay", "Elakay Value")),
            h("th", null, t("h_cjsync_col_cj", "CJ Value"))
          )),
          h("tbody", null, ...pendingFieldRows.map((row) => h("tr", { key: row.field },
            h("td", null, row.field),
            h("td", null, h("span", { className: "cjsync-badge cjsync-badge--different" }, row.elakayValue)),
            h("td", null, row.cjValue)
          )))
        )
      )
    ),

    (mode === "sync" && Array.isArray(compareData?.comparison) && compareData.comparison.length) ? h("div", { className: "cjwiz-shipping-mode" },
      h("h4", { className: "cjwiz-subheading" }, t("h_cjwiz_compare_title", "Field comparison (CJ vs current Elakay values)")),
      h("p", { className: "cjds-muted" }, t(
        "h_cjwiz_compare_note",
        "Informational only -- Finalize Synchronization re-applies every unlocked field automatically."
      )),
      h("div", { className: "cjds-table-wrap" },
        h("table", { className: "cjsync-table" },
          h("thead", null, h("tr", null,
            h("th", null, t("h_cjsync_col_field", "Field")),
            h("th", null, t("h_cjsync_col_elakay", "Elakay Value")),
            h("th", null, t("h_cjsync_col_cj", "CJ Value")),
            h("th", null, t("h_cjsync_col_status", "Status"))
          )),
          h("tbody", null, ...compareData.comparison.map((row) => h("tr", { key: row.field },
            h("td", null, row.field),
            h("td", null, JSON.stringify(row.elakayValue)),
            h("td", null, JSON.stringify(row.cjValue)),
            h("td", null, h("span", { className: `cjsync-badge cjsync-badge--${row.status}` }, row.status))
          )))
        )
      )
    ) : null,

    h("div", { className: "cjwiz-step__status cjwiz-step__status--ready" },
      h("span", { className: "cjwiz-badge cjwiz-badge--success" }, t("h_cjwiz_step3_ready", "Ready to finalize"))
    )
  );
};

module.exports = { CJSynchronizationStep };
